import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function UserGuide() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({
    dashboard: false,
    products: false,
    proDashboard: false,
    proProducts: false,
  });
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // ScrollSpy Effect
  useEffect(() => {
    const sections = document.querySelectorAll("main section[id], main div[id]");
    const observerOptions = {
      root: null,
      rootMargin: "-100px 0px -70% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute("id");
          if (id) {
            setActiveSection(id);
            // Auto-expand submenus on desktop if a child section is active
            if (id.startsWith("normal-record") || id === "normal-expense") {
              setOpenSubmenus((prev) => ({ ...prev, dashboard: true }));
            }
            if (id.startsWith("normal-product-")) {
              setOpenSubmenus((prev) => ({ ...prev, products: true }));
            }
            if (id.startsWith("pro-record") || id === "pro-expense") {
              setOpenSubmenus((prev) => ({ ...prev, proDashboard: true }));
            }
            if (id.startsWith("pro-product-")) {
              setOpenSubmenus((prev) => ({ ...prev, proProducts: true }));
            }
          }
        }
      });
    }, observerOptions);

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  // Scroll-to-Top Visibility Effect
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const openLightbox = (src: string) => {
    setLightboxImage(src);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    document.body.style.overflow = "";
  };

  const toggleSubmenu = (menu: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenSubmenus((prev) => ({ ...prev, [menu]: !prev[menu] }));
  };

  // Helper component for Sidebar Links (replaces repetitive HTML classes)
  const NavLink = ({ href, id, children, isSub = false, isPro = false }: any) => {
    const isActive = activeSection === id;

    // Base classes
    let classes = `block rounded-lg font-medium transition-colors ${isSub ? "pl-8 pr-3 py-1.5 text-xs" : "px-3 py-2 text-sm"
      } `;

    // Active vs Inactive state classes
    if (isActive) {
      classes += isPro ? "bg-amber-100/50 dark:bg-amber-900/40 text-amber-800 dark:text-amber-400 font-bold" : "bg-primary/5 text-primary font-bold";
    } else {
      if (isPro) {
        classes += "text-slate-700 dark:text-slate-400 hover:bg-amber-100/50 dark:hover:bg-amber-900/30 dark:hover:text-slate-200";
      } else if (isSub) {
        classes += "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200";
      } else {
        classes += "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200";
      }
    }

    return (
      <a
        href={href}
        onClick={() => {
          if (window.innerWidth < 768) setIsMobileMenuOpen(false);
        }}
        className={classes}
      >
        {children}
      </a>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col relative">

      {/* ── HEADER (Copied exactly from PrivacyPolicy.tsx) ── */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link href="/">
            <Logo size="md" />
          </Link>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <a
              href="https://play.google.com/store/apps/details?id=com.asldigitalsolutions.dailyhisaab"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-white hover:bg-primary/90 px-4 py-2 rounded-lg text-sm font-bold transition-all active:scale-95 shadow-sm flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" x2="12" y1="15" y2="3" />
              </svg>
              <span className="hidden sm:inline">Get the App</span>
              <span className="sm:hidden">Download</span>
            </a>
          </div>
        </div>
      </header>

      {/* ── MAIN LAYOUT ── */}
      <div className="container mx-auto px-4 py-8 flex flex-col md:flex-row gap-8 flex-1 max-w-7xl">

        {/* ── SIDEBAR NAVIGATION ── */}
        <aside className="w-full md:w-64 shrink-0 md:border-r border-border md:pr-6">
          <div className="md:sticky md:top-24 space-y-2">

            {/* Mobile Toggle Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-full md:hidden flex items-center justify-between bg-card border border-border px-4 py-3 rounded-xl font-bold text-foreground shadow-sm active:scale-[0.98] transition-all"
            >
              <span className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" x2="20" y1="12" y2="12" />
                  <line x1="4" x2="20" y1="6" y2="6" />
                  <line x1="4" x2="20" y1="18" y2="18" />
                </svg>
                Table of Contents
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${isMobileMenuOpen ? "rotate-180" : ""}`}>
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            {/* Navigation Links Container */}
            <div className={`space-y-8 pt-4 md:pt-0 pb-4 md:pb-0 ${isMobileMenuOpen ? "block" : "hidden md:block"}`}>

              {/* GETTING STARTED */}
              <div>
                <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-3 px-1">Getting Started</h3>
                <ul className="space-y-1">
                  <li><NavLink href="#download" id="download">Download & Install</NavLink></li>
                  <li><NavLink href="#signup" id="signup">Sign Up Process</NavLink></li>
                  <li><NavLink href="#setup" id="setup">Business Setup</NavLink></li>
                </ul>
              </div>

              {/* NORMAL MODE */}
              <div>
                <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-3 px-1">Normal Mode (Free)</h3>
                <ul className="space-y-1">

                  {/* Dashboard with Submenu */}
                  <li>
                    <div className="flex items-center justify-between rounded-lg hover:bg-slate-100 group pr-1 transition-colors">
                      <div className="flex-1"><NavLink href="#normal-dashboard" id="normal-dashboard">Dashboard</NavLink></div>
                      <button onClick={(e) => toggleSubmenu('dashboard', e)} className="md:block hidden text-slate-400 hover:text-slate-600 p-1 rounded focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${openSubmenus.dashboard ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
                      </button>
                    </div>
                    <ul className={`mt-1 mb-2 space-y-1 ${openSubmenus.dashboard ? 'block' : 'block md:hidden'}`}>
                      <li><NavLink href="#normal-record-sale" id="normal-record-sale" isSub>Record a Sale</NavLink></li>
                      <li><NavLink href="#normal-record-share" id="normal-record-share" isSub>Record & Share</NavLink></li>
                      <li><NavLink href="#normal-expense" id="normal-expense" isSub>Expense</NavLink></li>
                    </ul>
                  </li>

                  <li><NavLink href="#normal-transactions" id="normal-transactions">Transactions</NavLink></li>

                  {/* Products with Submenu */}
                  <li>
                    <div className="flex items-center justify-between rounded-lg hover:bg-slate-100 group pr-1 transition-colors">
                      <div className="flex-1"><NavLink href="#normal-products" id="normal-products">Products</NavLink></div>
                      <button onClick={(e) => toggleSubmenu('products', e)} className="md:block hidden text-slate-400 hover:text-slate-600 p-1 rounded focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${openSubmenus.products ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
                      </button>
                    </div>
                    <ul className={`mt-1 mb-2 space-y-1 ${openSubmenus.products ? 'block' : 'block md:hidden'}`}>
                      <li><NavLink href="#normal-product-scan" id="normal-product-scan" isSub>Scan Barcode</NavLink></li>
                      <li><NavLink href="#normal-product-manual" id="normal-product-manual" isSub>Manual Add</NavLink></li>
                      <li><NavLink href="#normal-product-edit" id="normal-product-edit" isSub>Edit Product</NavLink></li>
                      <li><NavLink href="#normal-product-delete" id="normal-product-delete" isSub>Delete Product</NavLink></li>
                    </ul>
                  </li>

                  <li><NavLink href="#normal-settings" id="normal-settings">Settings</NavLink></li>
                </ul>
              </div>

              {/* PRO MODE */}
              <div className="bg-gradient-to-b from-amber-50 dark:from-transparent dark:bg-transparent to-transparent p-3 -mx-3 rounded-2xl border border-amber-100/50 dark:border-transparent">
                <h3 className="text-[11px] font-black uppercase tracking-widest text-amber-600 mb-3 px-1 flex items-center gap-1.5">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                  </svg>
                  Pro Mode
                </h3>
                <ul className="space-y-1">
                  <li>
                    <div className="flex items-center justify-between rounded-lg hover:bg-amber-100/50 group pr-1 transition-colors">
                      <div className="flex-1"><NavLink href="#pro-dashboard" id="pro-dashboard" isPro>Dashboard</NavLink></div>
                      <button onClick={(e) => toggleSubmenu('proDashboard', e)} className="md:block hidden text-amber-400 hover:text-amber-600 p-1 rounded focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${openSubmenus.proDashboard ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
                      </button>
                    </div>
                    <ul className={`mt-1 mb-2 space-y-1 ${openSubmenus.proDashboard ? 'block' : 'block md:hidden'}`}>
                      <li><NavLink href="#pro-record-sale" id="pro-record-sale" isSub isPro>Record a Sale</NavLink></li>
                    </ul>
                  </li>
                  <li><NavLink href="#pro-transactions" id="pro-transactions" isPro>Transactions</NavLink></li>
                  <li>
                    <div className="flex items-center justify-between rounded-lg hover:bg-amber-100/50 group pr-1 transition-colors">
                      <div className="flex-1"><NavLink href="#pro-products" id="pro-products" isPro>Products</NavLink></div>
                      <button onClick={(e) => toggleSubmenu('proProducts', e)} className="md:block hidden text-amber-400 hover:text-amber-600 p-1 rounded focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${openSubmenus.proProducts ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
                      </button>
                    </div>
                    <ul className={`mt-1 mb-2 space-y-1 ${openSubmenus.proProducts ? 'block' : 'block md:hidden'}`}>
                      <li><NavLink href="#pro-product-scan" id="pro-product-scan" isSub isPro>Scan Barcode</NavLink></li>
                      <li><NavLink href="#pro-product-manual" id="pro-product-manual" isSub isPro>Manual Add</NavLink></li>
                      <li><NavLink href="#pro-product-edit" id="pro-product-edit" isSub isPro>Edit Product</NavLink></li>
                      <li><NavLink href="#pro-product-delete" id="pro-product-delete" isSub isPro>Delete Product</NavLink></li>
                    </ul>
                  </li>
                  <li><NavLink href="#pro-settings" id="pro-settings" isPro>Settings</NavLink></li>
                </ul>
              </div>

            </div>
          </div>
        </aside>

        {/* ── CONTENT AREA ── */}
        <main className="flex-1 bg-background border border-border rounded-2xl p-6 md:p-10 shadow-sm min-w-0 mb-8 md:mb-0">

          <header className="mb-10 pb-6 border-b border-border">
            <h1 className="text-3xl md:text-4xl font-black mb-3 text-foreground tracking-tight">App User Guide</h1>
            <p className="text-slate-500 text-lg">Everything you need to know to set up DailyHisaab and start managing your business efficiently.</p>
          </header>

          <div className="space-y-16">

            {/* GETTING STARTED SECTION */}
            <div>
              <h2 className="text-[11px] font-black uppercase tracking-widest text-primary mb-6 border-b border-primary/10 pb-2">Getting Started</h2>

              <section id="download" className="scroll-mt-24 mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold shrink-0">1</div>
                  <h3 className="text-2xl font-bold text-foreground tracking-tight">Download & Install</h3>
                </div>
                <p className="mb-6 text-slate-600 leading-relaxed">
                  DailyHisaab is built specifically for Android. To get started, download the official app directly from the Google Play Store. This ensures you always have the latest billing features and security updates.
                </p>
                <a href="https://play.google.com/store/apps/details?id=com.asldigitalsolutions.dailyhisaab" target="_blank" rel="noopener noreferrer" className="block transition-transform hover:scale-[1.01] active:scale-[0.99]">
                  <img src="https://dailyhisaab.com/dailyhisaab-on-play-store.png" alt="DailyHisaab on Play Store" className="w-full rounded-xl border border-border shadow-sm mb-6 object-cover" />
                </a>
              </section>

              <section id="signup" className="scroll-mt-24 mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold shrink-0">2</div>
                  <h3 className="text-2xl font-bold text-foreground tracking-tight">Sign Up Process</h3>
                </div>
                <p className="mb-4 text-slate-600 leading-relaxed">
                  DailyHisaab features a secure, password-free login system. We use WhatsApp OTP to keep your account safe while making sign-ins effortless.
                </p>
                <ol className="list-decimal list-inside space-y-2 mb-6 text-slate-600 ml-2">
                  <li><strong>Launch</strong> the DailyHisaab app.</li>
                  <li><strong>Enter</strong> your WhatsApp mobile number and tap <strong>Get WhatsApp OTP</strong>.</li>
                  <li><strong>Verify</strong> by entering the secure code sent directly to your WhatsApp.</li>
                </ol>
                <p className="text-sm text-slate-500 italic mb-6">
                  Click the image below to view it in full screen.
                </p>
                <img
                  src="https://dailyhisaab.com/DailyHisaab-Signup-Process.gif"
                  alt="DailyHisaab Sign Up Process"
                  onClick={() => openLightbox("https://dailyhisaab.com/DailyHisaab-Signup-Process.gif")}
                  className="lightbox-img w-full rounded-xl border border-border shadow-sm mb-6 object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                />
              </section>

              <section id="setup" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold shrink-0">3</div>
                  <h3 className="text-2xl font-bold text-foreground tracking-tight">Business Setup</h3>
                </div>
                <p className="mb-4 text-slate-600 leading-relaxed">
                  Setting up your business profile ensures your invoices look professional and accurate.
                </p>
                <ol className="list-decimal list-inside space-y-2 mb-6 text-slate-600 ml-2">
                  <li>Tap <strong>Get Started</strong> on the welcome screen.</li>
                  <li>Enter your <strong>Business Name</strong> and <strong>Email Address</strong>. (Leave the phone number as is for the verified badge; you can update it later). Tap <strong>Next Step</strong>.</li>
                  <li>Enter your complete <strong>Business Address</strong>, then tap <strong>Next Step</strong>.</li>
                  <li>Add your <strong>Business Website</strong> and tap <strong>Next Step</strong> (or tap <strong>Skip for Now</strong>).</li>
                  <li>If you were invited, confirm your <strong>Referral Code</strong>.</li>
                  <li>Tap <strong>Complete Setup</strong> to access your new DailyHisaab Dashboard!</li>
                </ol>
                <p className="text-sm text-slate-500 italic mb-6">
                  Click the image below to view the process in full screen.
                </p>
                <img
                  src="https://dailyhisaab.com/Business-Setup-Step.gif"
                  alt="DailyHisaab Business Setup Process"
                  onClick={() => openLightbox("https://dailyhisaab.com/Business-Setup-Step.gif")}
                  className="lightbox-img w-full rounded-xl border border-border shadow-sm mb-6 object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                />
              </section>
            </div>

            {/* NORMAL MODE SECTION */}
            <div className="pt-8 border-t border-border">
              <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-6 border-b border-slate-100 pb-2">Normal Mode Features</h2>

              <section id="normal-dashboard" className="scroll-mt-24 mb-12">
                <h3 className="text-2xl font-bold text-foreground tracking-tight mb-4">Dashboard</h3>
                <p className="mb-8 text-slate-600 leading-relaxed">
                  Your Dashboard is the central hub for all daily operations. From here, you can quickly log sales, generate bills, and track your daily expenses.
                </p>

                <div id="normal-record-sale" className="mb-10 scroll-mt-24">
                  <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-primary rounded-full"></span>
                    Record a Sale
                  </h4>
                  <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                    Quickly log a standard sale by adding products, adjusting quantities, and saving the record to your transactions. Click the image to view the process in full screen.
                  </p>
                  <img
                    src="https://dailyhisaab.com/normal-record-a-sale.gif"
                    alt="Record a Sale Process"
                    onClick={() => openLightbox("https://dailyhisaab.com/normal-record-a-sale.gif")}
                    className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                  />
                </div>

                <div id="normal-record-share" className="mb-10 scroll-mt-24">
                  <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-primary rounded-full"></span>
                    Record & Share
                  </h4>
                  <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                    Need to send an invoice to a customer? Use the Record & Share option to generate a digital bill and instantly send it via WhatsApp.
                  </p>
                  <img
                    src="https://dailyhisaab.com/normal-record-share.gif"
                    alt="Record & Share Process"
                    onClick={() => openLightbox("https://dailyhisaab.com/normal-record-share.gif")}
                    className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                  />
                </div>

                <div id="normal-expense" className="scroll-mt-24">
                  <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-primary rounded-full"></span>
                    Expense
                  </h4>
                  <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                    Easily log your daily shop expenses (like tea, transport, or supplies) right from the dashboard to keep your daily cash tracking accurate.
                  </p>
                  <img
                    src="https://dailyhisaab.com/normal-expense-record-transactions.gif"
                    alt="Record Expense Process"
                    onClick={() => openLightbox("https://dailyhisaab.com/normal-expense-record-transactions.gif")}
                    className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                  />
                </div>
              </section>

              <section id="normal-transactions" className="scroll-mt-24 mb-12">
                <h3 className="text-2xl font-bold text-foreground tracking-tight mb-4">Transactions</h3>
                <p className="mb-6 text-slate-600 leading-relaxed">
                  View your recent sales history. Keep track of all generated invoices and basic daily totals directly from the transactions tab.
                </p>
                <img
                  src="https://dailyhisaab.com/normal-transactions.gif"
                  alt="Transactions"
                  onClick={() => openLightbox("https://dailyhisaab.com/normal-transactions.gif")}
                  className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                />
              </section>

              <section id="normal-products" className="scroll-mt-24 mb-12">
                <h3 className="text-2xl font-bold text-foreground tracking-tight mb-4">Products</h3>
                <p className="mb-8 text-slate-600 leading-relaxed">
                  Build your catalog by adding products with their names and prices so you can select them quickly during checkout.
                </p>

                <div id="normal-product-scan" className="mb-10 scroll-mt-24">
                  <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-primary rounded-full"></span>
                    Scan Barcode
                  </h4>
                  <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                    Quickly add products by scanning their barcodes using your device's camera. Click the image to view the process in full screen.
                  </p>
                  <img
                    src="https://dailyhisaab.com/normal-add-product-barcode-scan.gif"
                    alt="Scan Barcode Product Process"
                    onClick={() => openLightbox("https://dailyhisaab.com/normal-add-product-barcode-scan.gif")}
                    className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                  />
                </div>

                <div id="normal-product-manual" className="mb-10 scroll-mt-24">
                  <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-primary rounded-full"></span>
                    Manual Add
                  </h4>
                  <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                    Add new products to your inventory manually by entering the item name, price, and other details. Click the image to view the process in full screen.
                  </p>
                  <img
                    src="https://dailyhisaab.com/normal-add-product-manual.gif"
                    alt="Manual Add Product Process"
                    onClick={() => openLightbox("https://dailyhisaab.com/normal-add-product-manual.gif")}
                    className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                  />
                </div>

                <div id="normal-product-edit" className="mb-10 scroll-mt-24">
                  <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-primary rounded-full"></span>
                    Edit Product
                  </h4>
                  <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                    Update the price, name, or stock details of an existing product in your catalog.
                  </p>
                  <img
                    src="https://dailyhisaab.com/normal-product-edit.gif"
                    alt="Edit Product Process"
                    onClick={() => openLightbox("https://dailyhisaab.com/normal-product-edit.gif")}
                    className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                  />
                </div>

                <div id="normal-product-delete" className="scroll-mt-24">
                  <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-primary rounded-full"></span>
                    Delete Product
                  </h4>
                  <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                    Remove products you no longer sell from your inventory to keep your catalog clean.
                  </p>
                  <img
                    src="https://dailyhisaab.com/normal-delete-product.gif"
                    alt="Manual Delete Product Process"
                    onClick={() => openLightbox("https://dailyhisaab.com/normal-delete-product.gif")}
                    className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                  />
                </div>
              </section>

              <section id="normal-settings" className="scroll-mt-24 mb-12">
                <h3 className="text-2xl font-bold text-foreground tracking-tight mb-4">Settings</h3>
                <p className="mb-6 text-slate-600 leading-relaxed">
                  Manage your basic business profile, update your contact details, and adjust simple app preferences.
                </p>
                <img
                  src="https://dailyhisaab.com/normal-settings-page.gif"
                  alt="Normal Settings Page"
                  onClick={() => openLightbox("https://dailyhisaab.com/normal-settings-page.gif")}
                  className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                />
              </section>
            </div>

            {/* PRO MODE SECTION */}
            <div className="pt-8 border-t border-border">
              <h2 className="text-[11px] font-black uppercase tracking-widest text-amber-500 mb-6 border-b border-amber-100 pb-2 flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
                Pro Mode Features
              </h2>

              <section id="pro-dashboard" className="scroll-mt-24 mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-2xl font-bold text-foreground tracking-tight">Dashboard (Pro)</h3>
                </div>
                <p className="mb-6 text-slate-600 leading-relaxed">
                  Unlock unlimited billing. Scan product barcodes instantly using your phone's camera, apply advanced GST calculations, and share professional PDF invoices directly via WhatsApp from your Pro dashboard.
                </p>

                <div id="pro-record-sale" className="mb-10 scroll-mt-24">
                  <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-5 flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-amber-500 rounded-full"></span>
                    Record a Sale
                  </h4>

                  {/* Subsection 1: Manual */}
                  <div className="mb-10 ml-3 pl-4 border-l-2 border-slate-100 dark:border-slate-800">
                    <h5 className="text-md font-bold text-slate-800 dark:text-slate-200 mb-3">1. Record a Sale Manually</h5>
                    <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                      Quickly tap products from your inventory grid to add them to the cart and generate a bill.
                    </p>
                    <img
                      src="http://dailyhisaab.com/pro-dashboard-record-a-sale-manually.gif"
                      alt="Record a Sale Manually"
                      onClick={() => openLightbox("http://dailyhisaab.com/pro-dashboard-record-a-sale-manually.gif")}
                      className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                    />
                  </div>

                  {/* Subsection 2: Barcode */}
                  <div className="mb-6 ml-3 pl-4 border-l-2 border-slate-100 dark:border-slate-800">
                    <h5 className="text-md font-bold text-slate-800 dark:text-slate-200 mb-3">2. Record a Sale using Barcode</h5>
                    <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                      Use your device's camera as a barcode scanner to instantly pull up products and add them to the current invoice.
                    </p>
                    <img
                      src="http://dailyhisaab.com/pro-record-sale-barcode-scan.gif"
                      alt="Record a Sale Using Barcode"
                      onClick={() => openLightbox("http://dailyhisaab.com/pro-record-sale-barcode-scan.gif")}
                      className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                    />
                  </div>
                </div>
              </section>

              <section id="pro-transactions" className="scroll-mt-24 mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-2xl font-bold text-foreground tracking-tight">Transactions (Pro)</h3>
                </div>
                <p className="mb-6 text-slate-600 leading-relaxed">
                  Access deep insights and advanced filtering for your transaction history. Identify trends, filter by date ranges, and view detailed metrics on your top-performing days.
                </p>
                <img
                  src="https://dailyhisaab.com/pro-transactions.gif"
                  alt="Pro Transactions"
                  onClick={() => openLightbox("https://dailyhisaab.com/pro-transactions.gif")}
                  className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                />
              </section>
            </div>

            <section id="pro-products" className="scroll-mt-24 mb-12">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-2xl font-bold text-foreground tracking-tight">Products (Pro)</h3>
              </div>
              <p className="mb-6 text-slate-600 leading-relaxed">
                Manage a massive inventory. Add barcode numbers to products for instant scanning, track detailed stock levels, and organize your catalog effortlessly from both mobile and the web portal.
              </p>

              <div id="pro-product-scan" className="mb-10 scroll-mt-24">
                <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-amber-500 rounded-full"></span>
                  Scan Barcode
                </h4>
                <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                  Quickly add products by scanning their barcodes using your device's camera. Click the image to view the process in full screen.
                </p>
                <img
                  src="https://dailyhisaab.com/pro-add-product-barcode-scan.gif"
                  alt="Scan Barcode Product Process"
                  onClick={() => openLightbox("https://dailyhisaab.com/pro-add-product-barcode-scan.gif")}
                  className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                />
              </div>

              <div id="pro-product-manual" className="mb-10 scroll-mt-24">
                <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-amber-500 rounded-full"></span>
                  Manual Add
                </h4><p className="mb-4 text-slate-600 leading-relaxed text-sm">
                  Add new products to your inventory manually by entering the item name, price, and other details. Click the image to view the process in full screen.
                </p>
                <img
                  src="https://dailyhisaab.com/pro-add-product.gif"
                  alt="Manual Add Product"
                  onClick={() => openLightbox("https://dailyhisaab.com/pro-add-product.gif")}
                  className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                />
              </div>

              <div id="pro-product-edit" className="mb-10 scroll-mt-24">
                <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-amber-500 rounded-full"></span>
                  Edit Product
                </h4>
                <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                  Update the price, name, or stock details of an existing product in your catalog.
                </p>
                <img
                  src="https://dailyhisaab.com/pro-edit-product.gif"
                  alt="Pro Edit Product"
                  onClick={() => openLightbox("https://dailyhisaab.com/pro-edit-product.gif")}
                  className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                />
              </div>

              <div id="pro-product-delete" className="scroll-mt-24">
                <h4 className="text-lg font-bold text-slate-800 tracking-tight mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-amber-500 rounded-full"></span>
                  Delete Product
                </h4>
                <p className="mb-4 text-slate-600 leading-relaxed text-sm">
                  Remove products you no longer sell from your inventory to keep your catalog clean.
                </p>
                <img
                  src="https://dailyhisaab.com/pro-product-delete.gif"
                  alt="Pro Delete Product"
                  onClick={() => openLightbox("https://dailyhisaab.com/pro-product-delete.gif")}
                  className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                />
              </div>
            </section>

            <section id="pro-settings" className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-2xl font-bold text-foreground tracking-tight">Settings (Pro)</h3>
              </div>
              <p className="mb-6 text-slate-600 leading-relaxed">
                Take full control of your business. Customize advanced invoice layouts, setup printers, download reports and manage your Pro subscription status directly from settings.
              </p>
              <img
                src="https://dailyhisaab.com/pro-settings.gif"
                alt="Pro Settings"
                onClick={() => openLightbox("https://dailyhisaab.com/pro-settings.gif")}
                className="lightbox-img w-full rounded-xl border border-border shadow-sm object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
              />
            </section>
          </div>
        </main >
      </div >

      {/* ── FOOTER (Copied exactly from PrivacyPolicy.tsx) ── */}
      < footer className="border-t border-border py-8 mb-safe" >
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <Link href="/">
              <Logo size="sm" />
            </Link>

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <Link href="/privacy" className="hover:text-primary transition-colors whitespace-nowrap">
                Privacy Policy
              </Link>
              <Link href="/terms" className="hover:text-primary transition-colors whitespace-nowrap">
                Terms of Service
              </Link>
              <Link href="/data-deletion" className="hover:text-primary transition-colors whitespace-nowrap">
                Data Deletion Policy
              </Link>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Made with ❤️ in Bengal by{" "}
                <a
                  href="https://asldigitalsolutions.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold hover:underline"
                >
                  ASL Digital Solutions
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                ©2026 DailyHisaab.🇮🇳 All Rights Reserved
              </p>
            </div>
          </div>
        </div>
      </footer >

      {/* ── SCROLL TO TOP BUTTON (Mobile Only) ── */}
      < button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 z-[90] md:hidden bg-primary text-white p-3 rounded-full shadow-xl transition-all duration-300 focus:outline-none ${showScrollTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"
          }`
        }
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m18 15-6-6-6 6" />
        </svg>
      </button >

      {/* ── LIGHTBOX MODAL ── */}
      {
        lightboxImage && (
          <div
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm cursor-zoom-out transition-opacity duration-300 opacity-100"
            onClick={closeLightbox}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={lightboxImage}
                alt="Full screen preview"
                className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeLightbox();
                }}
                className="absolute top-4 right-4 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full p-2 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
        )
      }
    </div >
  );
}